import { supabase } from '../lib/supabase';
import { signedExtraAmount } from '../utils/extraSign';

const extrasBudgetService = {
  async getByProject(projectId) {
    try {
      // Fetch project_extras for this project, including forecast_amount
      const { data: extras, error: extrasError } = await supabase
        ?.from('project_extras')
        ?.select('id, title, type, amount, cost_accepted, forecast_amount, co_number, created_at')
        ?.eq('project_id', projectId)
        ?.order('created_at', { ascending: true });

      if (extrasError) throw extrasError;
      if (!extras || extras?.length === 0) return [];

      const extraIds = extras?.map(e => e?.id);

      // Fetch approved/sent change orders linked to these extras via source_extra_id
      const { data: changeOrders, error: coError } = await supabase
        ?.from('subcontract_change_orders')
        ?.select('source_extra_id, amount')
        ?.in('source_extra_id', extraIds)
        ?.in('status', ['signed', 'sent']);

      if (coError) throw coError;

      // Fetch real expenses linked to these extras (approved or paid only)
      const { data: expenses, error: expError } = await supabase
        ?.from('project_expenses')
        ?.select('extra_id, total_amount')
        ?.in('extra_id', extraIds)
        ?.in('status', ['approved', 'paid']);

      if (expError) throw expError;

      // Build commitment map: source_extra_id -> SUM(amount)
      const commitmentMap = {};
      (changeOrders || [])?.forEach(co => {
        const eid = co?.source_extra_id;
        if (eid) {
          commitmentMap[eid] = (commitmentMap?.[eid] || 0) + (parseFloat(co?.amount) || 0);
        }
      });

      // Build expenses map: extra_id -> SUM(total_amount)
      const expensesMap = {};
      (expenses || [])?.forEach(exp => {
        const eid = exp?.extra_id;
        if (eid) {
          expensesMap[eid] = (expensesMap?.[eid] || 0) + (parseFloat(exp?.total_amount) || 0);
        }
      });

      // Map each extra to the required shape
      return extras?.map(e => {
        // Valeur SIGNEE : un credit vaut -X. L'engagement (les avenants) porte
        // deja son signe, donc les deux se comparent sur la meme base.
        const initialValue = signedExtraAmount(e, e?.cost_accepted ?? e?.amount);
        const commitment = commitmentMap?.[e?.id] || 0;
        const expensesTotal = expensesMap?.[e?.id] || 0;
        const forecast = parseFloat(e?.forecast_amount) || 0;
        const pl = initialValue - (commitment + expensesTotal + forecast);
        const plPercent = initialValue !== 0 ? (pl / Math.abs(initialValue)) * 100 : 0;

        return {
          id: e?.id,
          title: e?.title,
          type: e?.type,
          coNumber: e?.co_number,
          initialValue,
          commitment,
          expenses: expensesTotal,
          forecast,
          pl,
          plPercent,
        };
      });
    } catch (error) {
      console.error('[extrasBudgetService] getByProject error:', error);
      return [];
    }
  },

  async updateForecast(extraId, forecastAmount) {
    try {
      const { error } = await supabase
        ?.from('project_extras')
        ?.update({ forecast_amount: parseFloat(forecastAmount) || 0 })
        ?.eq('id', extraId);
      if (error) throw error;
    } catch (error) {
      console.error('[extrasBudgetService] updateForecast error:', error);
    }
  }
};

export default extrasBudgetService;
